import React, { useState } from 'react';
import { Input, Button, Icon } from '@iwoca/orion';
import { t } from '@lingui/macro';
import { useApplicationTable } from 'applicationTable/ApplicationTable.context';
import clsx from 'clsx';
import { useApplicationTableState } from 'contexts/ApplicationTableStateContext/ApplicationTableStateContext';
import { useLocale } from 'hooks/useLocale';
import { FormProvider, useForm } from 'react-hook-form';
import { TSearchBarFormData } from 'types';
import { APPLICATION_TABLE_KEYS } from 'utils/applications/applications.config';
import { RecentSearches } from './RecentSearches';
import styles from './SearchBar.module.css';

export const SearchBar = () => {
  const {
    searchApplication,
    state: { searchString },
  } = useApplicationTable();
  const [focused, setFocused] = useState(false);
  const { isDE } = useLocale();
  const { removeFilter } = useApplicationTableState();

  const formMethods = useForm<TSearchBarFormData>({
    shouldUnregister: false,
  });

  const { register, handleSubmit, setValue } = formMethods;

  function resetSearchBar() {
    formMethods.reset();
    searchApplication();
  }

  const onSubmit = ({ searchString }: TSearchBarFormData) => {
    removeFilter(APPLICATION_TABLE_KEYS.status);
    if (searchString.length === 0) {
      resetSearchBar();
    } else {
      searchApplication(searchString);
    }
  };

  const searchStringHasLength =
    formMethods.getValues().searchString?.length > 0;

  return (
    <FormProvider {...formMethods}>
      <form
        data-testid="searchBar"
        onSubmit={handleSubmit(onSubmit)}
        className={styles.searchForm}
      >
        <Input
          {...register('searchString')}
          type="text"
          name="searchString"
          id="searchString"
          className={clsx(styles.searchBar, isDE && styles.deSearchBar)}
          placeholder={t`Search apps...`}
          onFocus={() => {
            setFocused(true);
          }}
          onBlur={() => {
            setTimeout(() => {
              setFocused(false);
            }, 100);
          }}
        />
        <Button
          variant="tertiary"
          type="submit"
          className={clsx(styles.button, searchString && styles.hideButton)}
        >
          <Icon icon="zoomOutline" />
        </Button>
        {(focused || searchString || searchStringHasLength) && (
          <Button
            variant="denim"
            data-testid="searchButton"
            className={styles.buttonSearch}
            onClick={handleSubmit(onSubmit)}
          >
            <Icon icon="zoomOutline" />
          </Button>
        )}
        {focused && <RecentSearches setValue={setValue} />}
        {(searchString || searchStringHasLength) && (
          <Button
            variant="tertiary"
            data-testid="clearButton"
            className={styles.buttonClear}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              resetSearchBar();
            }}
          >
            <Icon icon="plus" className={styles.clearIcon} />
          </Button>
        )}
      </form>
    </FormProvider>
  );
};
